import { lazy, Suspense } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import AuthGate from './components/auth/AuthGate';
import Header from './components/layout/Header';
import PageTransition from './components/layout/PageTransition';
import ScrollToTop from './components/layout/ScrollToTop';
import { ErrorBoundary } from './components/ui/ErrorBoundary';
import { RestaurantDetailSkeleton } from './components/ui/LoadingSkeleton';
import { ToastContainer } from './components/ui/ToastContainer';
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { CartDrawerProvider } from './contexts/CartDrawerContext';
import { DeliveryZoneProvider } from './contexts/DeliveryZoneContext';
import { ToastProvider } from './contexts/ToastContext';

// Lazy load route components for code splitting
const RestaurantListPage = lazy(() => import('./pages/RestaurantListPage'));
const RestaurantDetailPage = lazy(() => import('./pages/RestaurantDetailPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const MinaThemeDemo = lazy(() => import('./pages/MinaThemeDemo'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const OrderHistoryPage = lazy(() => import('./pages/OrderHistoryPage'));

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <ScrollToTop />
        <AuthProvider>
          <DeliveryZoneProvider>
            <CartProvider>
              <CartDrawerProvider>
                <ToastProvider>
                  <div className="min-h-screen bg-gray-50">
                    {/* Skip to main content link */}
                    <a href="#main-content" className="skip-link" aria-label="Skip to main content">
                      Skip to main content
                    </a>

                    {/* Header Navigation */}
                    <Header />

                    {/* Main content - constrained to left of cart on desktop */}
                    <main id="main-content" tabIndex={-1} className="lg:mr-96 bg-white">
                      <PageTransition>
                        <Suspense fallback={<RestaurantDetailSkeleton />}>
                          <Routes>
                            <Route path="/" element={<Navigate to="/restaurants" replace />} />
                            <Route path="/restaurants" element={<RestaurantListPage />} />
                            <Route
                              path="/restaurants/:restaurantId"
                              element={<RestaurantDetailPage />}
                            />
                            <Route path="/checkout" element={<CheckoutPage />} />
                            <Route path="/login" element={<LoginPage />} />
                            {/* Protected Routes - Requirements: 5.1, 8.1 */}
                            <Route
                              path="/profile"
                              element={
                                <AuthGate>
                                  <ProfilePage />
                                </AuthGate>
                              }
                            />
                            <Route
                              path="/orders"
                              element={
                                <AuthGate>
                                  <OrderHistoryPage />
                                </AuthGate>
                              }
                            />
                            <Route
                              path="/orders/:orderId"
                              element={
                                <AuthGate>
                                  <OrderHistoryPage />
                                </AuthGate>
                              }
                            />
                            <Route path="/mina-demo" element={<MinaThemeDemo />} />
                          </Routes>
                        </Suspense>
                      </PageTransition>
                    </main>

                    {/* Toast notifications */}
                    <ToastContainer />
                  </div>
                </ToastProvider>
              </CartDrawerProvider>
            </CartProvider>
          </DeliveryZoneProvider>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
